import React, { useEffect, useState, useCallback } from "react";
import { Button, message, Tooltip } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import BranchForm from "./BranchesForm"; // Form component for add/edit
import API from "../../api"; // API instance
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal"; // Import reusable confirmation modal
import { getUserRole } from "../../utils/auth";

export default function Branches() {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({ currentPage: 1, pageSize: 5 });
  const [user, setUser] = useState(null);

  // Fetch branches whenever the component mounts
  const getBranches = async () => {
    try {
      const response = await API.get("/stores");
      setBranches(response.data);
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };
  useEffect(() => {
    setUser(getUserRole())
    getBranches();
  }, []); 



  const handleAddOrEditBranch = async (branchData) => {
    try {
      if (isEdit) {
        // Update Branch
        const res =await API.put(`/stores/${selectedBranch?._id}`, branchData);
        if(res.status == 200) {

          message.success("Branch updated successfully!");
        }
      } else {
        // Add new Branch
        const res = await API.post("/stores", branchData);
        if(res.status==200){message.success("Branch added successfully!");}
      }
      getBranches(); // Refresh the Branch list
      setOpenDrawer(false);
    } catch (error) {
      console.error("Error saving Branch:", error);
      message.error(error.response?.data.message);
    }
  };

  // Function to handle branch deletion
  const handleDeleteBranch = useCallback(async () => {
    try {
      await API.delete(`/branches/${branchToDelete?._id}`);
      setBranches((prev) => prev.filter((branch) => branch?._id !== branchToDelete?._id));
      setIsDeleteModalVisible(false); // Close the modal after deletion
    } catch (err) {
      message.error(err.response?.data.message);
    }
  }, [branchToDelete]); // Only depend on `branchToDelete`

  // Function to set up editing mode
  const handleEditClick = (branch) => {
    setIsEdit(true);
    setSelectedBranch({
      ...branch,
      managerId: branch.managerId?._id || branch?.managerId, // Ensure managerId is just the ID
      billingPrefix: branch.billingPrefix || null, // Include billingPrefix
      logo: branch.logo || null, // Pass logo to edit form
    });
    setOpenDrawer(true);
  };

  // Function to show the delete confirmation modal
  const showDeleteModal = (branch) => {
    setBranchToDelete(branch);
    setIsDeleteModalVisible(true);
  };

  const API_BASE_URL = API.defaults.baseURL;
  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.currentPage - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      render: (logo) =>
        logo ? (
          <img
            src={logo.startsWith('http') ? logo : `${API_BASE_URL}${logo}`}
            alt="logo"
            style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6, border: '1px solid #eee' }}
          />
        ) : (
          <span style={{ color: '#bbb', fontSize: 12 }}>No Logo</span>
        ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: (
        <span>
          Billing Prefix{" "}
          <Tooltip title="Used for invoice generation (e.g., R3/25-26/0001)">
            <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: "4px" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: "billingPrefix",
      key: "billingPrefix",
      render: (billingPrefix) => (
        <span style={{ 
          fontWeight: "600", 
          color: "#1890ff",
          backgroundColor: "#f0f8ff",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px"
        }}>
          {billingPrefix || "Not Set"}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || <span style={{ color: '#bbb', fontSize: 12 }}>Not Set</span>,
    },
    user?.editPermission == true &&{
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            style={{ marginRight: 8 }}
          />
          {/* <Button
            icon={<DeleteOutlined />}
            onClick={() => showDeleteModal(record)} // Show delete confirmation
            danger
          /> */}
        </div>
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsEdit(false);
            setSelectedBranch(null);
            setOpenDrawer(true);
          }}
        >
          Add New Branch
        </Button>
      </div>

      {openDrawer && (
        <BranchForm
        open={openDrawer}
          setOpen={setOpenDrawer}
          isEdit={isEdit}
          initialValue={isEdit ? selectedBranch : null}
          onSubmit={handleAddOrEditBranch}
        />
      )}

      <CustomTable columns={columns} dataSource={branches}  onPaginationChange={(pagination) =>
          setPaginationInfo({ currentPage: pagination.current, pageSize: pagination.pageSize })
      }/>

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteBranch}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this branch? This action cannot be undone."
      />
    </div>
  );
}
